// Automatic language detection with an explicit, documented fallback order.
//
//   1. URL locale prefix   — `/fi/...` always wins (shareable, SSR-safe, SEO)
//   2. `?lang=` query      — explicit override in a link (also persisted)
//   3. Stored preference   — what the user last picked in the switcher
//   4. Browser locale      — navigator.languages / navigator.language
//   5. DEFAULT_LANG (en)   — final fallback
//
// Only the first two are visible during SSR; 3–5 are client-only, which is why
// the provider renders DEFAULT_LANG until hydration.

import { DEFAULT_LANG, LANGUAGES, isLangCode, type LangCode } from "./config";

export type LangSource = "url" | "query" | "stored" | "browser" | "default";

export interface DetectedLanguage {
  lang: LangCode;
  source: LangSource;
}

/** Locale prefix carried by the pathname, if any. */
export function langFromPath(pathname: string): LangCode | null {
  return /^\/fi(\/|$)/.test(pathname) ? "fi" : null;
}

/** `?lang=fi` / `?lang=fi-FI` style override. */
export function langFromQuery(search: string | null | undefined): LangCode | null {
  if (!search) return null;
  try {
    const raw = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("lang");
    return normalizeLang(raw);
  } catch {
    return null;
  }
}

/** Map any BCP-47 tag (`fi`, `fi-FI`, `sv-FI`) to a supported language code. */
export function normalizeLang(tag: string | null | undefined): LangCode | null {
  if (!tag) return null;
  const base = tag.trim().toLowerCase().split(/[-_]/)[0];
  return LANGUAGES.some((l) => l.code === base) ? (base as LangCode) : null;
}

/**
 * First supported language in the browser's preference list.
 * `sv-FI` (Finland-Swedish) resolves to Finnish because that is the closer of
 * the two locales we actually ship.
 */
export function detectBrowserLang(tags?: readonly string[] | null): LangCode | null {
  const list =
    tags ??
    (typeof navigator !== "undefined"
      ? ((navigator.languages && navigator.languages.length
          ? navigator.languages
          : [navigator.language]) as readonly string[])
      : null);
  if (!list) return null;
  for (const tag of list) {
    const direct = normalizeLang(tag);
    if (direct) return direct;
    if (/^sv[-_]fi$/i.test(tag.trim())) return "fi";
  }
  return null;
}

export function readStoredLang(storageKey: string): LangCode | null {
  if (typeof window === "undefined") return null;
  try {
    const saved = window.localStorage.getItem(storageKey);
    return isLangCode(saved) ? saved : null;
  } catch {
    return null;
  }
}

export interface ResolveInput {
  pathname: string;
  search?: string | null;
  stored?: LangCode | null;
  browser?: LangCode | null;
}

/** Apply the documented fallback order. */
export function resolveLanguage({ pathname, search, stored, browser }: ResolveInput): DetectedLanguage {
  const fromPath = langFromPath(pathname);
  if (fromPath) return { lang: fromPath, source: "url" };

  const fromQuery = langFromQuery(search);
  if (fromQuery) return { lang: fromQuery, source: "query" };

  if (stored) return { lang: stored, source: "stored" };

  const fromBrowser = browser ?? detectBrowserLang();
  if (fromBrowser) return { lang: fromBrowser, source: "browser" };

  return { lang: DEFAULT_LANG, source: "default" };
}
